import { Accordion } from '@gcl/react-core';
import * as React from 'react';
import './DetailPageRightMenu.scss';
import { routes } from '@routes/routes.models';
import { useCreateDraftProjectTransmittalIfNotExist } from '@transmittal/transmittal.queries';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { useTransmittalCreationModal } from '@transmittal/TransmittalCreationModal/transmittalCreationModal.hooks';
import {
  ERightMenuItemType,
  ETemporaryWorksFormsItemDataStatus,
  ETempWorkType,
  IDetailPageRightMenuTemporaryWorksFormsItem,
  IDetailPageRightMenuTemporaryWorksFormsItemData,
} from './detailPageRightMenuTemporaryWorksForms.models';
import { DetailPageRightMenuTemporaryWorksFormsItem } from './DetailPageRightMenuTemporaryWorksFormsItem';
import './DetailPageRightMenuTemporaryWorksForms.scss';
import { ESelectT1CreationModalSearchParamKey } from '@common/SelectT1CreationModal.constants';
import { EDesignBriefAction, EDesignBriefStatus } from '@designBrief/designBrief.constants';
import { EDesignModificationAction, EDesignModificationStatus } from '@designModification/designModification.models';
import { ELoadingRemovalCertActionType } from '@permitToLoadRemove/permitToLoadRemove.models';
import { ITransmittalItem } from '@transmittal/transmittal.models';
import { isDesignModificationStatusInitial, useAllowNewT3 } from '@designModification/designModification.hooks';
import { useAllowNewT4 } from '@permitToLoadRemove/permitToLoadRemove.hooks';
import { useAllowNewTransmittal } from '@transmittal/transmittalList.hooks';
import { envConfigs } from '@config/envConfigs';
import { useAllowNewT2 } from '@designBrief/designBrief.hooks';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { useQueryClient } from '@tanstack/react-query';
import { EDesignBriefDetailSearchParamKey } from '@designBrief/designBrief.models';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { useIsDesignerNotApplicable } from '@masterSchedule/masterSchedule.hooks';
import {
  useFetchCheckCertificatesByT1,
  useFetchCheckerTransmittalsByT1,
  useFetchDesignBriefsByT1,
  useFetchDesignerTransmittalsByT1,
  useFetchDesignModificationsByT1,
  useFetchLoadingRemovalCertsByT1,
  useFetchReviewerTransmittalsByT1,
  useFetchSiteTransmittalsByT1,
} from './DetailPageRightMenu.queries';
import { DetailPageRightMenuTemporaryWorksFormsItemData } from './DetailPageRightMenuTemporaryWorksFormsItemData';

interface IDetailPageRightMenuTemporaryWorksFormsProps {
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  masterScheduleId?: number;
  masterSchedule?: IMasterScheduleDetail;
  masterScheduleIsCancelled?: boolean;
  masterScheduleIsLocked?: boolean;
}

export const DetailPageRightMenuTemporaryWorksForms: React.FunctionComponent<
  IDetailPageRightMenuTemporaryWorksFormsProps
> = ({
  setIsDrawerOpen,
  masterScheduleId,
  masterSchedule,
  masterScheduleIsCancelled = false,
  masterScheduleIsLocked = false,
}) => {
  const { navigate, generateRoutePath } = useNavigate();
  const projectId = useProjectId();
  const isProjectArchived = useProjectIsArchived();
  const queryClient = useQueryClient();

  const isAllowNewT2 = useAllowNewT2();
  const isAllowNewT3 = useAllowNewT3();
  const isAllowNewT4 = useAllowNewT4();
  const isAllowNewDT = useAllowNewTransmittal(ETransmittalType.DESIGNER);
  const isAllowNewST = useAllowNewTransmittal(ETransmittalType.SITE);
  const isAllowNewICE = useAllowNewTransmittal(ETransmittalType.ICE);
  const isAllowNewLIR = useAllowNewTransmittal(ETransmittalType.REVIEWER);
  const isDesignerNotApplicable = useIsDesignerNotApplicable(masterSchedule);

  const fetchByT1Params = { projectId, projectMasterScheduleId: masterScheduleId ?? 0 };

  const { data: designBriefsByT1 } = useFetchDesignBriefsByT1(fetchByT1Params);

  const { data: designModificationsByT1 } = useFetchDesignModificationsByT1(fetchByT1Params);

  const { data: loadingRemovalCertsByT1 } = useFetchLoadingRemovalCertsByT1(fetchByT1Params);

  const { data: designerTransmittalsByT1 } = useFetchDesignerTransmittalsByT1(fetchByT1Params);

  const { data: siteTransmittalsByT1 } = useFetchSiteTransmittalsByT1(fetchByT1Params);

  const { data: checkerTransmittalsByT1 } = useFetchCheckerTransmittalsByT1(fetchByT1Params);
  const { data: reviewerTransmittalsByT1 } = useFetchReviewerTransmittalsByT1(fetchByT1Params);
  const { data: checkCertificatesByT1 } = useFetchCheckCertificatesByT1(fetchByT1Params);

  const checkCertificatesData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    return (
      checkCertificatesByT1?.map((certificate) => {
        return { ...certificate, status: undefined, id: certificate.id, title: certificate.certificateNo };
      }) ?? []
    );
  }, [JSON.stringify(checkCertificatesByT1)]);

  const designBriefData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    let status: ETemporaryWorksFormsItemDataStatus | undefined;
    return (designBriefsByT1 ?? []).map((t2) => {
      switch (t2.actioner) {
        case EDesignBriefAction.DRAFT:
          status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          break;
        case EDesignBriefAction.CANCELLED:
          status = ETemporaryWorksFormsItemDataStatus.CANCEL;
          break;
        case EDesignBriefAction.NO_ACTION:
          if (t2.status === EDesignBriefStatus.FINISHED) status = ETemporaryWorksFormsItemDataStatus.COMPLETE;
          break;
        case EDesignBriefAction.SRP:
          status = ETemporaryWorksFormsItemDataStatus.SRP;
          break;
        case EDesignBriefAction.TWC:
          status = ETemporaryWorksFormsItemDataStatus.TWC;
          break;
        case EDesignBriefAction.TWD:
          status = ETemporaryWorksFormsItemDataStatus.TWD;
          break;
        default:
          break;
      }
      return { id: t2.id, title: t2.designBriefNo, status };
    });
  }, [JSON.stringify(designBriefsByT1)]);

  const designModificationData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    let status: ETemporaryWorksFormsItemDataStatus | undefined;
    return (designModificationsByT1 ?? []).map((t3) => {
      switch (t3.actioner) {
        case EDesignModificationAction.DRAFT:
          status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          break;
        case EDesignModificationAction.CANCELLED:
          status = ETemporaryWorksFormsItemDataStatus.CANCEL;
          break;
        case EDesignModificationAction.NO_ACTION:
          if (isDesignModificationStatusInitial(t3.status)) status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          if (t3.status === EDesignModificationStatus.completed) status = ETemporaryWorksFormsItemDataStatus.COMPLETE;
          break;
        case EDesignModificationAction.ICE:
          status = ETemporaryWorksFormsItemDataStatus.ICE;
          break;
        case EDesignModificationAction.TWD:
          status = ETemporaryWorksFormsItemDataStatus.TWD;
          break;
        case EDesignModificationAction.TWC:
          status = ETemporaryWorksFormsItemDataStatus.TWC;
          break;
        case EDesignModificationAction.SRP:
          status = ETemporaryWorksFormsItemDataStatus.SRP;
          break;
        default:
          if (isDesignModificationStatusInitial(t3.status)) status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          if (t3.status === EDesignModificationStatus.referToDesignerTransmittal)
            status = ETemporaryWorksFormsItemDataStatus.COMPLETE;
          break;
      }
      return { id: t3.id, title: t3.designChangeNo, status };
    });
  }, [JSON.stringify(designModificationsByT1)]);

  const loadingRemovalCertsData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    let status: ETemporaryWorksFormsItemDataStatus | undefined;
    return (loadingRemovalCertsByT1 ?? []).map((t4) => {
      switch (t4.actioner) {
        case undefined:
        case null:
          status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          break;
        case ELoadingRemovalCertActionType.SRP:
          status = ETemporaryWorksFormsItemDataStatus.SRP;
          break;
        case ELoadingRemovalCertActionType.TWC:
          status = ETemporaryWorksFormsItemDataStatus.TWC;
          break;
        case ELoadingRemovalCertActionType.DESIGNER:
          status = ETemporaryWorksFormsItemDataStatus.TWD;
          break;
        case ELoadingRemovalCertActionType.ICE:
          status = ETemporaryWorksFormsItemDataStatus.ICE;
          break;
        case ELoadingRemovalCertActionType.PM:
          status = ETemporaryWorksFormsItemDataStatus.PM;
          break;
        case ELoadingRemovalCertActionType.CANCELLED:
          status = ETemporaryWorksFormsItemDataStatus.CANCEL;
          break;
        case ELoadingRemovalCertActionType.COMPLETED:
          status = t4.removalCertRequired
            ? ETemporaryWorksFormsItemDataStatus.COMPLETE_REMOVAL_REQUIRED
            : ETemporaryWorksFormsItemDataStatus.COMPLETE;
          break;
        default:
          break;
      }
      return { id: t4.id, title: t4.certificateNo, status };
    });
  }, [JSON.stringify(loadingRemovalCertsByT1)]);

  const getTransmittalData = (data: ITransmittalItem[]) => {
    let status: ETemporaryWorksFormsItemDataStatus | undefined;
    return data.map((d) => {
      switch (d.status) {
        case ETransmittalStatus.DRAFT:
        case undefined:
        case null:
          status = ETemporaryWorksFormsItemDataStatus.DRAFT;
          break;
        case ETransmittalStatus.APPROVED:
          status = ETemporaryWorksFormsItemDataStatus.COMPLETE;
          break;
        case ETransmittalStatus.PENDING_FOR_RFC:
          status = ETemporaryWorksFormsItemDataStatus.CHOP;
          break;
        default:
          break;
      }
      return { id: d.id, title: d.transmittalNo, status };
    });
  };

  const designerTransmittalData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    return getTransmittalData(designerTransmittalsByT1 ?? []);
  }, [JSON.stringify(designerTransmittalsByT1)]);

  const checkerTransmittalData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    return getTransmittalData(checkerTransmittalsByT1 ?? []);
  }, [JSON.stringify(checkerTransmittalsByT1)]);

  const siteTransmittalData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    return getTransmittalData(siteTransmittalsByT1 ?? []);
  }, [JSON.stringify(siteTransmittalsByT1)]);

  const reviewerTransmittalData: IDetailPageRightMenuTemporaryWorksFormsItemData[] = React.useMemo(() => {
    return getTransmittalData(reviewerTransmittalsByT1 ?? []);
  }, [JSON.stringify(reviewerTransmittalsByT1)]);

  const {
    renderModal: renderTransmittalCreationModal,
    openPortal: openTransmittalCreationModal,
    closePortal: closeTransmittalCreationModal,
  } = useTransmittalCreationModal({
    projectId,
    onSuccess: (type?: ETransmittalType) => {
      if (type === ETransmittalType.ICE)
        queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.CHECKER_TRANSMITTAL_BY_T1]);
      if (type === ETransmittalType.REVIEWER)
        queryClient.invalidateQueries([projectId, masterScheduleId, FETCH_QUERIES_NAME.CHECKER_TRANSMITTAL_BY_T1]); // to-do
      closeTransmittalCreationModal();
    },
  });

  const createTransmittalBaseParams = {
    projectId,
    masterScheduleId,
    remark: '',
  };

  const useCreateDraftDesignerTransmittalResult = useCreateDraftProjectTransmittalIfNotExist();
  const useCreateDraftSiteTransmittalResult = useCreateDraftProjectTransmittalIfNotExist();

  React.useEffect(() => {
    if (useCreateDraftDesignerTransmittalResult.isSuccess && !!useCreateDraftDesignerTransmittalResult.data?.item?.id) {
      setIsDrawerOpen(false);
      navigate(getTransmittalItemRoute(useCreateDraftDesignerTransmittalResult.data.item.type), {
        id: useCreateDraftDesignerTransmittalResult.data?.item?.id.toString(),
      });
    }
  }, [useCreateDraftDesignerTransmittalResult.isSuccess, useCreateDraftDesignerTransmittalResult.data]);

  React.useEffect(() => {
    if (useCreateDraftSiteTransmittalResult.isSuccess && !!useCreateDraftSiteTransmittalResult.data?.item?.id) {
      setIsDrawerOpen(false);
      navigate(getTransmittalItemRoute(useCreateDraftSiteTransmittalResult.data.item.type), {
        id: useCreateDraftSiteTransmittalResult.data?.item?.id.toString(),
      });
    }
  }, [useCreateDraftSiteTransmittalResult.isSuccess, useCreateDraftSiteTransmittalResult.data]);

  const tempWorkItems: IDetailPageRightMenuTemporaryWorksFormsItem[] = [
    {
      type: ETempWorkType.T2DesignBrief,
      onClickAdd: (e) => {
        navigate(routes.t2.create.path, {}, true, undefined, {
          [EDesignBriefDetailSearchParamKey.masterScheduleId]: masterScheduleId,
        });
      },
      data: designBriefData,
      onClickData: (data) => generateRoutePath(routes.t2.view.path, { id: data.id?.toString() }),
      isAddEnable:
        !isProjectArchived &&
        !isDesignerNotApplicable &&
        isAllowNewT2 &&
        !masterScheduleIsCancelled &&
        !masterScheduleIsLocked &&
        (designBriefsByT1?.length ?? 0) === 0,
      shouldDisableCount: isDesignerNotApplicable,
    },
    {
      type: ETempWorkType.T3DesignModifications,
      onClickAdd: () => {
        navigate(routes.t3.create.path, {}, true, undefined, {
          [ESelectT1CreationModalSearchParamKey.masterScheduleId]: masterScheduleId,
        });
      },
      data: designModificationData,
      onClickData: (data) => generateRoutePath(routes.t3.view.path, { id: data.id?.toString() }),
      isAddEnable: !isProjectArchived && isAllowNewT3 && !masterScheduleIsCancelled && !masterScheduleIsLocked,
    },
    {
      type: ETempWorkType.T4PermitsToLoadOrRemove,
      onClickAdd: () => {
        navigate(routes.t4.create.path, {}, true, undefined, {
          [ESelectT1CreationModalSearchParamKey.masterScheduleId]: masterScheduleId,
        });
      },
      data: loadingRemovalCertsData,
      onClickData: (data) => generateRoutePath(routes.t4.view.path, { id: data.id?.toString() }),
      isAddEnable: !isProjectArchived && isAllowNewT4 && !masterScheduleIsCancelled && !masterScheduleIsLocked,
    },
    {
      type: ETempWorkType.DesignerTransmittal,
      onClickAddMutationFn: useCreateDraftDesignerTransmittalResult,
      onClickAddMutationParam: { ...createTransmittalBaseParams, type: ETransmittalType.DESIGNER },
      queryKeyToInvalidAfterSuccessAdd: [projectId, masterScheduleId, FETCH_QUERIES_NAME.DESIGNER_TRANSMITTAL_BY_T1],
      data: designerTransmittalData,
      onClickData: (data) =>
        generateRoutePath(routes.transmittals.designerTransmittal.item.path, { id: data.id?.toString() }),
      isAddEnable:
        !isProjectArchived &&
        !isDesignerNotApplicable &&
        isAllowNewDT &&
        !masterScheduleIsCancelled &&
        !masterScheduleIsLocked,
      shouldDisableCount: isDesignerNotApplicable,
    },
    {
      type: ETempWorkType.SiteTransmittal,
      onClickAddMutationFn: useCreateDraftSiteTransmittalResult,
      onClickAddMutationParam: { ...createTransmittalBaseParams, type: ETransmittalType.SITE },
      queryKeyToInvalidAfterSuccessAdd: [projectId, masterScheduleId, FETCH_QUERIES_NAME.SITE_TRANSMITTAL_BY_T1],
      data: siteTransmittalData,
      onClickData: (data) =>
        generateRoutePath(routes.transmittals.siteTransmittal.item.path, { id: data.id?.toString() }),
      isAddEnable: !isProjectArchived && isAllowNewST && !masterScheduleIsCancelled && !masterScheduleIsLocked,
    },
    {
      type: ETempWorkType.CheckerTransmittal,
      onClickAdd: () => {
        openTransmittalCreationModal(ETransmittalType.ICE);
      },
      data: checkerTransmittalData,
      onClickData: (data) =>
        generateRoutePath(routes.transmittals.checkerTransmittal.item.path, { id: data.id?.toString() }),
      isAddEnable: !isProjectArchived && isAllowNewICE && !masterScheduleIsCancelled && !masterScheduleIsLocked,
    },
    {
      type: ETempWorkType.CheckCertificates,
      isAddEnable: false,
      data: checkCertificatesData,
    },
    ...(envConfigs.enableLir
      ? [
          {
            type: ETempWorkType.LambethInternalReview,
            isAddEnable: !isProjectArchived && isAllowNewLIR && !masterScheduleIsCancelled && !masterScheduleIsLocked,
            onClickAdd: () => {
              openTransmittalCreationModal(ETransmittalType.REVIEWER);
            },
            data: reviewerTransmittalData,
            onClickData: (data) =>
              generateRoutePath(routes.transmittals.lirTransmittal.item.path, { id: data.id?.toString() }),
          } as IDetailPageRightMenuTemporaryWorksFormsItem,
        ]
      : []),
  ];

  return (
    <div className="gcl-gems-detail-right-menu-forms">
      <ApiErrorDisplay
        errors={[
          { error: useCreateDraftDesignerTransmittalResult.error, genericError: 'Create transmittal failed' },
          { error: useCreateDraftSiteTransmittalResult.error, genericError: 'Create transmittal failed' },
        ]}
      />
      <Accordion
        allowClickHeaderToExpand
        content={tempWorkItems.map((item) => {
          return {
            disabled:
              isDesignerNotApplicable &&
              (item.type === ETempWorkType.DesignerTransmittal || item.type === ETempWorkType.T2DesignBrief),
            id: item.type,
            onRenderBody: () => {
              return (
                <div key={`${item.type}-body`}>
                  {item.data?.map((data, index) => {
                    return (
                      <DetailPageRightMenuTemporaryWorksFormsItemData
                        itemType={
                          item.type === ETempWorkType.CheckCertificates
                            ? ERightMenuItemType.DOWNLOAD
                            : ERightMenuItemType.LINK
                        }
                        data={data}
                        key={`${item.type}-body-${index.toString()}`}
                        onClickData={item.onClickData}
                      />
                    );
                  })}
                </div>
              );
            },
            onRenderHeader: () => {
              return <DetailPageRightMenuTemporaryWorksFormsItem item={item} key={`${item.type}-header`} />;
            },
            defaultIsExpanded: false,
          };
        })}
      />
      {renderTransmittalCreationModal({})}
    </div>
  );
};
